import FRAPageNotification from "Components/fra-m-page-notification/fra-m-page-notification";
import FRAAirlineFilter from "Components/fra-m-airline-filter/fra-m-airline-filter";
import FRALocationFilter from "Components/fra-m-location-filter/fra-m-location-filter";
import FRAFlights from "Components/fra-m-flights/fra-m-flights";
import { ViewportObserver } from "@kluntje/services";
import { Component } from "@kluntje/core";
import { STICKY, HIDDEN, SCROLL_LOCK } from "Helper/cssClasses";
import { addClass, onEvent, removeClass, toggleClass } from "@kluntje/js-utils/lib/dom-helpers";
import { throttle } from "Helper/functionHelper/decorators";
import {focusTrap, focusUponElement} from "Helper/domHelper/focusHelper";

export default class FRAStickyPageHeaderContainer extends Component {
  viewportObserver = ViewportObserver.getInstance();

  private lastScrollY = 0;

  private inViewport = false;

  private lockScroll = false;

  private placeholder = document.createElement("div");

  private timer = -1;

  private observer?: IntersectionObserver;

  private static readonly STANDBY_CLASS = "fra-state-sticky-standby";
  private static readonly NO_TRANSITION_CLASS = "fra-state-no-transition";

  constructor() {
    super({
      events: [
        {
          event: "scroll",
          target: "window",
          handler: "onScroll",
        },
        {
          event: FRAPageNotification.OPEN_EVENT,
          target: "window",
          handler: "onTrafficNotificationOpen",
        },
        {
          event: "kl-out-vp",
          target: "this",
          handler: "onScrollOut",
        },
        {
          event: FRAPageNotification.CLOSE_EVENT,
          target: "window",
          handler: "onTrafficNotificationClose",
        },
        {
          event: FRAAirlineFilter.AUTO_SCROLL,
          target: "window",
          handler: "onWindowAutoScroll",
        },
        {
          event: FRALocationFilter.AUTO_SCROLL,
          target: "window",
          handler: "onWindowAutoScroll",
        },
        {
          event: FRAFlights.AUTO_SCROLL,
          target: "window",
          handler: "onWindowAutoScroll",
        },
      ],
    });

    this.viewportObserver.observe(this);
  }

  /**
   * Creates a zero height element placed right before the sticky container to observe for viewport intersections
   * @override
   */
  afterComponentRender(): void {
    addClass(this.placeholder, "fra-m-sticky-page-header-container__placeholder", HIDDEN);
    if (this.parentNode !== null) {
      this.parentNode.insertBefore(this.placeholder, this);
    }

    onEvent(this.placeholder, "kl-in-vp", this.onReturnToStartingPoint, this);
    this.observer = new IntersectionObserver(FRAStickyPageHeaderContainer.placeholderIntersectionObserverCallback, {
      threshold: [0],
    });
    this.observer.observe(this.placeholder);
  }

  /**
   * Called once the sticky container has returned to its original place
   */
  onReturnToStartingPoint(): void {
    this.inViewport = true;
    removeClass(this, FRAStickyPageHeaderContainer.STANDBY_CLASS);
    addClass(this.placeholder, HIDDEN);
  }

  /**
   * Readies the sticky container by placing it on top just outside the viewport
   */
  onScrollOut(): void {
    this.inViewport = false;
    // disable animation to avoid container flickering
    addClass(this, FRAStickyPageHeaderContainer.NO_TRANSITION_CLASS);
    this.updatePlaceholderHeight();

    addClass(this, FRAStickyPageHeaderContainer.STANDBY_CLASS);
    removeClass(this.placeholder, HIDDEN);
    setTimeout(() => removeClass(this, FRAStickyPageHeaderContainer.NO_TRANSITION_CLASS), 500);
  }

  /**
   * (De-)activates the sticky behavior if the header is out of viewport and the page is scrolled up or down
   */
  @throttle(1500)
  onScroll(): void {
    clearTimeout(this.timer);
    // this will deactivate the sticky change of header when because of a page resize (e.g. orientation change) a scroll event was triggered
    if (document.documentElement.classList.contains(SCROLL_LOCK)) return;

    if (
      !this.lockScroll &&
      window.scrollY >= 0 &&
      document.documentElement.scrollHeight - window.innerHeight > window.scrollY
    ) {
      toggleClass(this, STICKY, !this.inViewport && this.isScrollUp());
      toggleClass(document.body, "fra-state-visible-header", !this.inViewport && this.isScrollUp());
    }
    this.lastScrollY = window.scrollY;
  }

  /**
   * On AUTO_SCROLL event of AirlineFilter/LocationFilter component
   * Or on scroll event for sticky flight-search in Flights overview
   * this component's scroll handler is temporary deactivated
   */
  onWindowAutoScroll(): void {
    this.lockScroll = true;
    setTimeout(() => (this.lockScroll = false), 3000);
    this.updatePlaceholderHeight();
  }

  /**
   * Every time the notification open event is dispatched this component's scroll handler is temporary deactivated
   * and the placeholder height is updated
   */
  onTrafficNotificationOpen(): void {
    this.lockScroll = true;
    focusUponElement(this);
    setTimeout(() => (this.lockScroll = false), 500);
    this.updatePlaceholderHeight();
  }

  /**
   * Updates the placeholder height every time the traffic notification is closed (and thus the header height
   * is changed)
   */
  onTrafficNotificationClose(): void {
    this.updatePlaceholderHeight();
  }

  /**
   * Updates the placeholder height to the component's current height
   */
  private updatePlaceholderHeight() {
    this.placeholder.style.height = `60px`;
  }

  /**
   * Determines if the user is currently scrolling up
   * @returns {boolean} - is scrolling up
   */
  private isScrollUp(): boolean {
    return window.scrollY - this.lastScrollY < 0;
  }

  private static placeholderIntersectionObserverCallback(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.dispatchEvent(new Event("kl-in-vp"));
      }
    }
  }
}

customElements.define("fra-m-sticky-page-header-container", FRAStickyPageHeaderContainer);
